import React from 'react'
import { useQuery } from 'react-query'
import { isEqual } from 'lodash'
import { Controller, useFormContext } from 'react-hook-form'
import makeStyles from '@mui/styles/makeStyles'
import { TextField } from '@mui/material'
import { Autocomplete } from '@mui/material'
import { apiJSON } from '@shared/utils/api/api.util'
import { Resource } from './admin-generic.types'
import { AdminGenericCrudSummaryTemplateComponent } from '@modules/admin-generic/admin-generic-crud-summary-template.component'
import { Template } from '@shared/types'
import { useRouter } from '@shared/hooks/use-router'
import { NO_OPTIONS_PLACEHOLDER } from '@shared/consts'

type Props = {
  multiple?: boolean
  defaultValue?: any
  label: string
  name: string
  resourceConfig: Resource
  filteredResults?: string[]
  filterField?: string
  isDisabled?: boolean
}

type RouteQuery = {
  resourceID: string
}

export const AdminGenericCrudSelect = ({
  multiple,
  defaultValue,
  label,
  name,
  resourceConfig,
  filteredResults,
  filterField,
  isDisabled,
}: Props) => {
  const { control } = useFormContext()
  const classes = useStyles()
  const { query } = useRouter<RouteQuery>()
  const { data } = useQuery(resourceConfig.endpoint, () =>
    apiJSON(`${resourceConfig.endpoint}`)
  )
  const includesSummaryTemplates = Boolean(
    data?.find((element: any) => element.summaryTemplates)
  )

  if (includesSummaryTemplates) {
    const templateData = data.find(
      (element: Template) => element.descriptionTemplateID === +query.resourceID
    )

    return (
      <AdminGenericCrudSummaryTemplateComponent
        templateData={templateData}
        endpoint={resourceConfig.endpoint}
      />
    )
  }

  if (!data) return null

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={(defaultValue ?? multiple) ? [] : {}}
      render={(formControllerProps) => (
        <Autocomplete
          value={formControllerProps.field.value}
          autoHighlight
          multiple={!includesSummaryTemplates && multiple}
          className={classes.autocomplete}
          disabled={isDisabled}
          options={
            filteredResults
              ? data.filter((option: any) =>
                  filteredResults.includes(option?.[filterField || ''])
                )
              : data
          }
          getOptionLabel={(option: { [key: string]: string }) =>
            option?.name ?? option?.[`${resourceConfig?.singular}Name`] ?? ''
          }
          isOptionEqualToValue={isEqual}
          renderInput={(params) => (
            <TextField {...params} label={label} variant="outlined" disabled={isDisabled} />
          )}
          onChange={(e, data) => formControllerProps.field.onChange(data)}
          filterSelectedOptions
          {...formControllerProps}
          noOptionsText={NO_OPTIONS_PLACEHOLDER}
        />
      )}
    />
  )
}

/**
 * @deprecated use theme styles
 */
export const useStyles = makeStyles((theme) => ({
  autocomplete: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))
